import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

/**
 * Long introduction text. From `md` up: the paragraph as it always was. Below `md`: ONE line of the real text
 * (ellipsis) followed by « Read more », which opens the full text in a dialog (closable with the button, the ✕,
 * Escape or a tap outside; focus handled by Radix). The page's text itself stays in the HTML, in a single copy,
 * visible as one line — nothing is hidden for the search engines; the dialog only mounts when opened.
 *
 * `title`: heading of the dialog (accessible name).
 * `always`: the same one-line + « Read more » presentation at every width (used by the desktop scooters intro).
 */
export const CollapsibleText = ({ children, title, className = '', testId, always = false }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const toggleRef = useRef(null);
  return (
    <div className={always ? 'flex min-w-0 flex-1 items-center gap-2' : 'max-md:flex max-md:items-center max-md:gap-2'}>
      <div className={`${className} ${always ? 'min-w-0 flex-1 truncate' : 'max-md:min-w-0 max-md:flex-1 max-md:truncate'}`} data-testid={testId}>
        {children}
      </div>
      <button
        ref={toggleRef}
        type="button"
        onClick={() => setOpen(true)}
        className={`${always ? '' : 'md:hidden'} shrink-0 inline-flex min-h-11 items-center text-sm font-medium text-accent ${always ? 'lg:min-h-9' : ''}`}
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        {t('common.readMore')}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid={testId ? `${testId}-dialog` : undefined}
          onCloseAutoFocus={(e) => { e.preventDefault(); if (toggleRef.current) toggleRef.current.focus(); }}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <div className="text-base text-ink-light leading-relaxed">{children}</div>
          <DialogFooter>
            <button type="button" onClick={() => setOpen(false)} className="btn-primary min-h-11 w-full sm:w-auto" data-testid={testId ? `${testId}-close` : undefined}>
              {t('common.close')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CollapsibleText;
